import { useQuery } from '@tanstack/react-query';
import { z } from 'zod/v4';
import type { operations } from '@molchanovs/api-contract';
import { createQuery } from '@shared/lib/query-helpers';

export type CatalogResponse =
  operations['getApiV1GearErpProducts']['responses'][200]['content']['application/json'];
export type CatalogProduct = CatalogResponse['data']['items'][number];
type CatalogRequest = NonNullable<operations['getApiV1GearErpProducts']['parameters']['query']>;

const requestSchema = z
  .object({
    q: z.string().trim().max(200).optional(),
    limit: z.number().int().min(1).max(100),
    cursor: z.string().min(1).max(4096).optional(),
  })
  .strict();

export const catalogResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        id: z.string().regex(/^(0|[1-9]\d*)$/),
        sku: z.string().nullable(),
        name: z.string().nullable(),
      })
    ),
  }),
  meta: z.object({
    limit: z.number().int().min(1).max(100),
    nextCursor: z.string().nullable(),
    hasNextPage: z.boolean(),
  }),
});

export const catalogKeys = {
  page: (scope: string, params: CatalogRequest) => ['product-catalog', scope, params] as const,
};

export function catalogQuery(scope: string, params: CatalogRequest) {
  const validated = requestSchema.parse(params);
  return createQuery<CatalogResponse>({
    path: '/api/v1/gear-erp/products',
    queryKey: catalogKeys.page(scope, validated),
    schema: catalogResponseSchema,
    requestOptions: { params: validated },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCatalog(scope: string, params: CatalogRequest, enabled: boolean) {
  return useQuery({
    ...catalogQuery(scope, params),
    enabled: enabled && scope.length > 0,
    retry: false,
  });
}
